import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Transaction } from '@/types';
import { FundsApiServiceSupabase, StorageProvider, useUserAccounts, useAccountData } from '@trader-dashboard/shared';
import { isDemoAccount, getDemoFundsTransactions, getDemoAccountData } from '@/data/demoAccountHelper';

const webStorage: StorageProvider = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
};

// Create singleton instance of the Supabase Funds API
const fundsApi = new FundsApiServiceSupabase(webStorage);

type FundsStats = {
  totalDeposits?: number;
  totalWithdrawals?: number;
  currentBalance?: number;
  profit?: number;
  unrealizedProfit?: number;
  allTimeBalance?: number;
  allTimeProfit?: number;
};

interface FundsContextType {
  loaded: boolean;

  transactions: Transaction[];
  fundsStats: FundsStats;

  add: (accountId: string, type: 'deposit' | 'withdrawal', amount: number, date: Date) => Promise<void>;
  update: (accountId: string, id: number, type: 'deposit' | 'withdrawal', amount: number, date: Date) => Promise<void>;
  refresh: (accountId: string) => Promise<void>;
}

const FundsContext = createContext<FundsContextType | undefined>(undefined);

interface FundsProviderProps {
  children: React.ReactNode;
}

export const FundsProvider: React.FC<FundsProviderProps> = ({ children }) => {
  const [loaded, setLoaded] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [fundsStats, setFundsStats] = useState<FundsStats>({
  });

  const {selectedAccountId} = useUserAccounts();
  const {loaded: accountDataLoaded, accountData} = useAccountData();
  
  const calculateFundsStats = useCallback( (transactions: Transaction[], balanceOverride?: number):FundsStats => {
    let totalDeposits = 0;
    let totalWithdrawals = 0;

    const currentBalance = balanceOverride ?? ((accountData && accountData.balance) || 0);

    let allTimeBalance = currentBalance;
    let allTimeProfit = currentBalance;

    transactions.forEach(transaction => {
      // Handle both old (type) and new (transaction_type) field names
      const transactionType = transaction.transaction_type || transaction.type;

      if (transactionType === 'deposit') {
        // For new format, amount is already positive
        const depositAmount = Math.abs(transaction.amount);
        totalDeposits += depositAmount;
        allTimeProfit -= depositAmount;
      } else if (transactionType === 'withdrawal') {
        // For new format, amount is negative, so we need absolute value
        const withdrawalAmount = Math.abs(transaction.amount);
        totalWithdrawals += withdrawalAmount;
        allTimeBalance += withdrawalAmount;
        allTimeProfit += withdrawalAmount;
      }
    });

    const equity = totalDeposits - totalWithdrawals;

    const profit = currentBalance - equity;
    const unrealizedProfit = currentBalance - (totalDeposits - totalWithdrawals);

    return {
      totalDeposits,
      totalWithdrawals,
      currentBalance,
      profit,
      unrealizedProfit,
      allTimeBalance,
      allTimeProfit,
    }
  }, [accountData]);

  const load = useCallback(async (accountId: string, ignoreCache: boolean) => {
    console.log("Loading funds data for account", accountId);
    try {
      setLoaded(false);

      if (isDemoAccount(accountId)) {
        const demoTransactions = getDemoFundsTransactions() as Transaction[];
        const demoBalance = getDemoAccountData().balance;
        setTransactions(demoTransactions);
        setFundsStats(calculateFundsStats(demoTransactions, demoBalance));
        setLoaded(true);
        return;
      }

      if (!accountDataLoaded) return;

      const response = await fundsApi.getFundsTransactions(accountId, ignoreCache);
      if (!response) {
        console.log("No funds transactions");
        setTransactions([]);
        setFundsStats({});
        setLoaded(true);
        return;
      }

      let transactions = response.transactions;

      setTransactions(transactions);
      setFundsStats(calculateFundsStats(transactions));
      setLoaded(true);
    } catch (error) {
      console.error('Error loading funds data:', error);
      setLoaded(true);
    }
  }, [accountDataLoaded, calculateFundsStats]);

  useEffect(() => {
    if (!selectedAccountId) return;

    load(selectedAccountId, false);
  }, [selectedAccountId, load]);

  const add = useCallback(async (accountId: string, type: 'deposit' | 'withdrawal', amount: number, date: Date) => {
    try {
      await fundsApi.addTransaction(accountId, type, amount, date);
      await load(accountId, true);
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  }, [load]);

  const update = useCallback(async (accountId: string, id: number, type: 'deposit' | 'withdrawal', amount: number, date: Date) => {
    try {
      await fundsApi.updateTransaction(accountId, id, type, amount, date);
      await load(accountId, true);
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  }, [load]);

  const refresh = useCallback(async (accountId: string) => {
    await load(accountId, true);
  }, [load]);

  const contextValue = {
    loaded,

    transactions,
    fundsStats,

    add,
    update,
    refresh,
  };

  return <FundsContext.Provider value={contextValue}>{children}</FundsContext.Provider>;
};


export const useFunds = () => {
  const context = useContext(FundsContext);
  
  if (context === undefined) {
    throw new Error('useFunds must be used within a FundsProvider');
  }

  return context;
};